import { isEqual } from 'lodash';

import {
  AugmentedCreatePresentationPageInput,
  AugmentedUpdatePresentationPageInput,
} from '@/types/schema';

import { NEW_PAGE_ID_SENTINEL } from '../../ClientPresentationDesigner.utils';
import { ClientPresentationV2Page } from '../../types/ClientPresentation.PresentationPageType';
import { mapPageToCreateInput } from '../mapPageToCreateInput';
import { PresentationSaveContext } from '../presentationSave.types';
import { canWritePageContent, diffPageContent } from './diffPageContent';

export interface PagesDiff {
  withPages: AugmentedCreatePresentationPageInput[];
  updatePages: AugmentedUpdatePresentationPageInput[];
  removePageIDs: string[];
}

const EMPTY_DIFF: PagesDiff = {
  withPages: [],
  updatePages: [],
  removePageIDs: [],
};

export function isEmptyPagesDiff(diff: PagesDiff): boolean {
  return (
    diff.withPages.length === 0 &&
    diff.updatePages.length === 0 &&
    diff.removePageIDs.length === 0
  );
}

/** Returns false when an existing page has unsupported content changes. */
export function canWritePages(
  previous: ClientPresentationV2Page[],
  next: ClientPresentationV2Page[]
): boolean {
  const previousById = new Map(previous.map((page) => [page.id, page]));

  return next.every((page) => {
    const before = previousById.get(page.id);
    if (!before) return true;
    if (isEqual(before, page)) return true;
    return canWritePageContent(before, page);
  });
}

/** Diffs page creation, removal, and order against the saved baseline. */
export function diffPages({
  previous,
  next,
  context,
}: {
  previous: ClientPresentationV2Page[];
  next: ClientPresentationV2Page[];
  context: PresentationSaveContext;
}): PagesDiff {
  if (isEqual(previous, next)) return EMPTY_DIFF;

  const previousIndexById = new Map(
    previous.map((page, index) => [page.id, index])
  );
  const previousById = new Map(previous.map((page) => [page.id, page]));
  const nextIds = new Set(next.map((page) => page.id));

  const withPages: AugmentedCreatePresentationPageInput[] = [];
  const updatePages: AugmentedUpdatePresentationPageInput[] = [];

  next.forEach((page, index) => {
    if (!previousIndexById.has(page.id)) {
      withPages.push(mapPageToCreateInput(page, context.householdId, index));
      return;
    }
    const moved = previousIndexById.get(page.id) !== index;
    const before = previousById.get(page.id);
    const content = before ? diffPageContent(before, page, context) : null;

    if (moved || content) {
      updatePages.push({
        id: page.id,
        update: moved ? { index } : {},
        ...content,
      });
    }
  });

  const removePageIDs = previous
    .map((page) => page.id)
    .filter((id) => !nextIds.has(id) && !id.startsWith(NEW_PAGE_ID_SENTINEL));

  return { withPages, updatePages, removePageIDs };
}
